import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import { Box, ButtonBase, IconButton, Tooltip, Typography } from '@mui/material';
import { AutoAwesome, DeleteOutline, LockOutlined, TuneOutlined } from '@mui/icons-material';
import TemplateFieldPopover from './TemplateFieldPopover';
import { FIELD_KIND_LABELS, FIELD_SOURCE_LABELS } from './fieldLabels';
import useEditorEditable from './useEditorEditable';

/**
 * One visual language for AI fields in three sizes (inline chip, heading chip, block card):
 * the same dashed border, light AI tint, ✦ icon and corner radius. The heading size is the
 * inline chip inside a heading (sized in editorContentStyles).
 */
const AI_BORDER_COLOR = 'var(--q-ai-border, #C9C1F2)';
const AI_TINT = 'var(--q-ai-tint, #F9F8FE)';
const AI_TINT_ACTIVE = 'var(--q-ai-tint-strong, #F2EFFD)';
const AI_TEXT = 'var(--q-ai-text, #3A28B8)';
const AI_RADIUS = 'var(--q-radius-sm, 6px)';

/**
 * Whether an element's text is clipped (line clamp). Re-measured when the text or width changes.
 * @param {React.RefObject<HTMLElement>} ref - Clamped element.
 * @param {string} text - Its text (re-measure trigger).
 * @param {boolean} enabled - Measure only while clamped.
 * @returns {boolean} True when the text overflows its clamp.
 */
const useIsClamped = (ref, text, enabled) => {
  const [clamped, setClamped] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return undefined;
    const measure = () => setClamped(el.scrollHeight - el.clientHeight > 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, text, enabled]);
  return clamped;
};

/**
 * React node view shared by `templateField` (inline chip) and `templateFieldBlock` (card).
 * Clicking opens {@link TemplateFieldPopover} when the editor is editable.
 *
 * The block card stays quiet at rest (a ✦ in the margin, the instruction clamped to two lines)
 * and shows its label row (label, output type, source, actions) on hover, selection or keyboard
 * focus inside it.
 *
 * @param {object} props - TipTap node view props.
 * @returns {JSX.Element}
 */
const TemplateFieldView = ({ node, editor, selected, updateAttributes, deleteNode }) => {
  const isBlock = node.type.name === 'templateFieldBlock';
  const anchorRef = useRef(null);
  const promptRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const editable = useEditorEditable(editor);
  const { instruction, kind, required, source, maxWords, diffBlock, tplLocked } = node.attrs;

  useEffect(() => {
    const runtime = editor?.storage?.quillRuntime;
    if (runtime && runtime.pendingEditFieldId && runtime.pendingEditFieldId === node.attrs.id) {
      runtime.pendingEditFieldId = null;
      // Defer one frame so the anchor element is laid out before the popover measures it.
      const raf = requestAnimationFrame(() => setOpen(true));
      return () => cancelAnimationFrame(raf);
    }
    return undefined;
  }, [editor, node.attrs.id]);

  const label = instruction || 'Describe what the AI should write here';
  const clamped = useIsClamped(promptRef, label, isBlock && !expanded);

  /**
   * Open the popover from a click or keyboard activation.
   * @param {React.SyntheticEvent} event - Triggering event.
   * @returns {void}
   */
  const handleActivate = (event) => {
    if (!editor?.isEditable) return;
    event.preventDefault();
    setOpen(true);
  };

  const kindLabel = FIELD_KIND_LABELS[kind] || kind;
  const meta = [kindLabel, maxWords ? `max ${maxWords} words` : null].filter(Boolean).join(' · ');
  const popover = editable && (
    <TemplateFieldPopover
      anchorEl={anchorRef.current}
      open={open}
      attrs={{ instruction, kind, required, source, maxWords }}
      isBlock={isBlock}
      onCommit={updateAttributes}
      onDelete={deleteNode}
      onConvert={(toBlock) => editor?.chain().focus().convertTemplateField(node.attrs.id, toBlock).run()}
      onClose={() => setOpen(false)}
    />
  );

  if (!isBlock) {
    return (
      <NodeViewWrapper as="span" className="q-tpl-field-inline" data-tpl-field={node.attrs.id}>
        <Box
          component="span"
          ref={anchorRef}
          className="q-tpl-chip"
          data-testid="tpl-chip"
          role={editable ? 'button' : undefined}
          tabIndex={editable ? 0 : undefined}
          // The name only applies to the button role; read-only chips are read from their text,
          // which includes the visible "optional" suffix.
          aria-label={editable ? `AI field${required === false ? ' (optional)' : ''}: ${label}` : undefined}
          title={`AI field${required === false ? ' (optional)' : ''}: ${label}`}
          data-optional={required === false ? 'true' : undefined}
          onClick={handleActivate}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleActivate(e); }}
          contentEditable={false}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            maxWidth: 360,
            verticalAlign: 'baseline',
            px: 0.75,
            py: '1px',
            mx: '1px',
            borderRadius: AI_RADIUS,
            border: '1px dashed',
            borderColor: selected ? AI_TEXT : AI_BORDER_COLOR,
            bgcolor: selected ? AI_TINT_ACTIVE : AI_TINT,
            color: AI_TEXT,
            fontSize: '0.86em',
            fontWeight: 500,
            lineHeight: 1.5,
            cursor: editable ? 'pointer' : 'default',
            textDecoration: diffBlock === 'delete' ? 'line-through' : 'none',
            '&:hover': editable ? { borderColor: AI_TEXT } : undefined,
          }}
        >
          <AutoAwesome sx={{ fontSize: '0.95em', flexShrink: 0 }} />
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: instruction ? 'normal' : 'italic' }}>
            {label}
          </Box>
          {required === false && (
            <Box component="span" data-testid="tpl-chip-optional" sx={{ flexShrink: 0, fontSize: '0.85em', fontWeight: 500, color: 'text.secondary' }}>
              · optional
            </Box>
          )}
        </Box>
        {popover}
      </NodeViewWrapper>
    );
  }

  const borderColor = diffBlock === 'delete'
    ? 'error.main'
    : diffBlock === 'insert'
      ? 'success.main'
      : selected ? AI_TEXT : AI_BORDER_COLOR;
  const revealed = '&:hover .q-tpl-reveal, &:focus-within .q-tpl-reveal, &[data-selected="true"] .q-tpl-reveal';
  const atRest = '&:hover .q-tpl-rest, &:focus-within .q-tpl-rest, &[data-selected="true"] .q-tpl-rest';

  return (
    <NodeViewWrapper as="div" className="q-tpl-field-block" data-tpl-field={node.attrs.id} data-drag-handle="">
      <Box
        ref={anchorRef}
        contentEditable={false}
        role="group"
        tabIndex={editable ? 0 : undefined}
        aria-label={`AI field${required === false ? ' (optional)' : ''}${tplLocked ? ' (locked)' : ''}, ${kindLabel}: ${label}`}
        data-selected={selected ? 'true' : undefined}
        onClick={handleActivate}
        onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) handleActivate(e); }}
        sx={{
          position: 'relative',
          mt: 2,
          mb: required === false || tplLocked ? 2 : 1.5,
          px: 1.75,
          pt: 1.25,
          pb: 1.25,
          borderRadius: AI_RADIUS,
          border: '1px dashed',
          borderColor,
          bgcolor: selected ? AI_TINT_ACTIVE : AI_TINT,
          cursor: editable ? 'pointer' : 'default',
          opacity: diffBlock === 'delete' ? 0.6 : 1,
          transition: 'border-color 120ms ease, background-color 120ms ease',
          '&:hover': editable ? { borderColor: diffBlock ? borderColor : AI_TEXT } : undefined,
          '&:focus-visible': { outline: '2px solid var(--q-focus-ring, #4F3BD6)', outlineOffset: 2 },
          '& .q-tpl-reveal': { opacity: 0, pointerEvents: 'none', transition: 'opacity 120ms ease' },
          [revealed]: { opacity: 1, pointerEvents: 'auto' },
          '& .q-tpl-rest': { transition: 'opacity 120ms ease' },
          [atRest]: { opacity: 0 },
        }}
      >
        {/* At rest: a small ✦ in the page margin marks the field. */}
        <Box
          className="q-tpl-rest"
          aria-hidden="true"
          sx={{ position: 'absolute', left: -26, top: 12, display: 'flex', color: AI_TEXT, pointerEvents: 'none' }}
        >
          <AutoAwesome sx={{ fontSize: 15 }} />
        </Box>

        {/* On hover, selection or focus: label, output type, source and actions, sitting on the
            top border so revealing them never moves the page. */}
        <Box
          className="q-tpl-reveal"
          data-testid="tpl-field-label-row"
          sx={{
            position: 'absolute',
            top: 0,
            left: 10,
            right: 10,
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              height: 22,
              px: 0.75,
              borderRadius: AI_RADIUS,
              bgcolor: 'var(--q-surface, #FFFFFF)',
              border: '1px solid',
              borderColor: AI_BORDER_COLOR,
              color: AI_TEXT,
              minWidth: 0,
            }}
          >
            <AutoAwesome sx={{ fontSize: 13 }} />
            <Typography component="span" noWrap sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              AI field{required === false ? ' · optional' : ''}
            </Typography>
            <Typography component="span" noWrap sx={{ fontSize: 11.5, color: 'text.secondary', ml: 0.25 }}>
              {meta}
            </Typography>
            {source && source !== 'any' && (
              <Typography component="span" noWrap sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                · {FIELD_SOURCE_LABELS[source] || source}
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: 1 }} />
          {editable && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                borderRadius: AI_RADIUS,
                bgcolor: 'var(--q-surface, #FFFFFF)',
                border: '1px solid',
                borderColor: AI_BORDER_COLOR,
              }}
            >
              <Tooltip title="Edit field">
                <IconButton
                  size="small"
                  aria-label="Edit AI field"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(true);
                  }}
                  sx={{ p: 0.25, color: 'text.secondary', borderRadius: AI_RADIUS }}
                >
                  <TuneOutlined sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove field">
                <IconButton
                  size="small"
                  aria-label="Remove AI field"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteNode();
                  }}
                  sx={{ p: 0.25, color: 'text.secondary', borderRadius: AI_RADIUS }}
                >
                  <DeleteOutline sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Always visible: whether the field is optional or locked (no hover needed). */}
        {(required === false || tplLocked) && (
          <Box
            className="q-tpl-rest-tags"
            data-testid="tpl-field-rest-tags"
            sx={{ position: 'absolute', bottom: 0, right: 10, transform: 'translateY(50%)', display: 'flex', gap: 0.5, pointerEvents: 'none' }}
          >
            {required === false && (
              <Box component="span" sx={{ height: 18, px: 0.75, display: 'inline-flex', alignItems: 'center', borderRadius: AI_RADIUS, bgcolor: 'var(--q-surface, #FFFFFF)', border: '1px solid', borderColor: AI_BORDER_COLOR, fontSize: 11, fontWeight: 500, color: 'text.secondary' }}>
                Optional
              </Box>
            )}
            {tplLocked && (
              <Box component="span" sx={{ height: 18, px: 0.5, display: 'inline-flex', alignItems: 'center', gap: 0.25, borderRadius: AI_RADIUS, bgcolor: 'var(--q-surface, #FFFFFF)', border: '1px solid', borderColor: AI_BORDER_COLOR, fontSize: 11, fontWeight: 500, color: 'text.secondary' }}>
                <LockOutlined sx={{ fontSize: 12 }} />
                Locked
              </Box>
            )}
          </Box>
        )}
        <Typography
          component="p"
          ref={promptRef}
          data-testid="tpl-field-prompt"
          sx={{
            m: 0,
            fontSize: 14.5,
            color: instruction ? 'text.primary' : 'text.secondary',
            fontStyle: instruction ? 'normal' : 'italic',
            whiteSpace: 'pre-wrap',
            textDecoration: diffBlock === 'delete' ? 'line-through' : 'none',
            ...(expanded ? {} : {
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden',
            }),
          }}
        >
          {label}
        </Typography>
        {(clamped || expanded) && (
          <ButtonBase
            aria-expanded={expanded ? 'true' : 'false'}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
            sx={{
              mt: 0.25,
              fontSize: 12.5,
              fontWeight: 600,
              color: AI_TEXT,
              borderRadius: 'var(--q-radius-xs, 4px)',
              '&:hover': { textDecoration: 'underline' },
              '&.Mui-focusVisible': { outline: '2px solid var(--q-focus-ring, #4F3BD6)', outlineOffset: 1 },
            }}
          >
            {expanded ? 'Show less' : 'Show more'}
          </ButtonBase>
        )}
      </Box>
      {popover}
    </NodeViewWrapper>
  );
};

export default TemplateFieldView;
